import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { S3_MENU_FOLDER } from "../../store/constants";
import { fetchDishBand } from "../../store/menuActions";
import "./exploreStory.css";
import { ToastContainer, toast } from "react-toastify";
import { menuActions } from "../../store/menuSlice";
import { bottomBarActions } from "../../store/bottomBarSlice";
import mixpanel from "mixpanel-browser";
import {
  getDisplayChefTimeLabel,
  getTodayDateValue,
} from "../../utils/timeHelper";
import moment from "moment";
import ImageLoader from "../chef/ImageLoader";
import { addDish, removeDish, updatePortion } from "../../store/cartActions";
import { calculateCurrentPrice, combineTheDate } from "../../utils/helper";
import ChefCardSkeleton from "../ChefListScreen/ChefCardSkeleton";
import { Player } from "video-react";
import { uniq } from "lodash";
import { useSwiperSlide } from "swiper/react";
import classNames from "classnames";

// Image srcs that have already loaded, so a remounted slide doesn't flash the
// spinner again while the browser serves the image from cache.
const loadedImageSrcs = new Set();

const VideoPlayer = memo(
  ({
    videoUrl,
    isVideoActiveOnScreen,
    isPaused,
    action,
    isVideoReadyToPlay,
    setIsVideoReadyToPlay,
    onReady,
    imageLoaded,
    isPlaying: isPlayingVideo,
    playerRef,
  }) => {
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
      setIsPlaying(isPlayingVideo);
    }, [isPlayingVideo]);

    const handleReady = () => {
      onReady();
      imageLoaded();
    };

    useEffect(() => {
      const player = playerRef?.current;
      if (!player) return;
      if (isPaused) {
        player.pause();
        action("pause");
        setIsPlaying(false);
      } else {
        player.play();
        action("play");
        setIsPlaying(true);
      }
    }, [isPaused]);

    useEffect(() => {
      const player = playerRef?.current;
      if (!player) return;
      if (isVideoActiveOnScreen) {
        player.play();
        setIsPlaying(true);
      } else {
        player.pause();
      }
    }, [isVideoActiveOnScreen]);

    const handlePlay = () => {
      setIsVideoReadyToPlay(true);
    };

    useEffect(() => {
      const player = playerRef?.current;
      if (!player) return;

      const handleReadyStateChange = () => {
        handlePlay();
        handleReady();
      };

      const handleTimeUpdate = () => {
        if (player.currentTime >= 2) {
          player.pause();
          setIsPlaying(false);
        }
      };

      const videoElement = player.video.video;

      videoElement.addEventListener("loadeddata", handleReadyStateChange);
      videoElement.addEventListener("timeupdate", handleTimeUpdate);

      return () => {
        videoElement.removeEventListener("loadeddata", handleReadyStateChange);
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      };
    }, []);

    return (
      <div>
        {!isVideoReadyToPlay && <ChefCardSkeleton darkMode />}
        <div className="player-wrapper">
          <div className="explore-video-player">
            <div className="videoContainer" id="videoContainer">
              <Player
                ref={playerRef}
                src={videoUrl}
                autoPlay
                width="100%"
                playsInline
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const LandingStories = forwardRef((props, ref) => {
  const {
    exploreIndex,
    storyIndex,
    action,
    isPaused,
    label,
    onGeoLocationClickHandler,
    handleCancel,
    handleNextSlide
  } = props;

  const swiperIndexSelector = useSelector(
    (store) => store.storie.landingSwiperIndex
  );

  const playerRef = useRef(null);

  const dispatch = useDispatch();
  const isVideoActiveOnScreen = swiperIndexSelector === exploreIndex;

  const handleTouchStart = (e) => {
    e.preventDefault();
    action("next");
  };

  useEffect(() => {
    if (storyIndex === 0) {
      const left = document.getElementById(`left-touch-zone${exploreIndex}`);
      const right = document.getElementById(`right-touch-zone${exploreIndex}`);

      if (left) {
        left.addEventListener("touchstart", handleTouchStart, {
          passive: false,
        });
      }
      if (right) {
        right.addEventListener("touchstart", handleTouchStart, {
          passive: false,
        });
      }

      return () => {
        if (left) {
          left.removeEventListener("touchstart", handleTouchStart);
        }
        if (right) {
          right.removeEventListener("touchstart", handleTouchStart);
        }
      };
    }
  }, [exploreIndex]);

  const [loaded, setLoaded] = useState(() =>
    loadedImageSrcs.has(label?.src)
  );
  const [isVideoReadyToPlay, setIsVideoReadyToPlay] = useState(false);

  useImperativeHandle(ref, () => ({
    mute: () => {
      if (playerRef.current) {
        playerRef.current.muted = true;
      }
    },
    unMute: () => {
      if (playerRef.current) {
        playerRef.current.muted = false;
      }
    },
  }));

  useEffect(() => {
    if (!props.id) return;
    dispatch(fetchDishBand(props.id));
  }, [dispatch]);

  useEffect(() => {
    if (!loaded && isVideoActiveOnScreen) {
      action("pause");
    }
  }, [loaded, isVideoActiveOnScreen]);
  // storyIndex

  useEffect(() => {
    if (loaded && isVideoActiveOnScreen) {
      if (storyIndex !== 0) {
        action("play");
      }
    }
  }, [loaded, isVideoActiveOnScreen]);

  useEffect(() => {
    if (!isPaused && !loaded) {
      action("pause");
    }
  }, [isPaused, loaded]);

  useEffect(() => {
    if (loaded && isVideoActiveOnScreen) {
      // action("play");
    }
  }, [loaded, isVideoActiveOnScreen]);

  useEffect(() => {
    if (!isVideoActiveOnScreen) {
      action("pause");
    }
  }, [isVideoActiveOnScreen]);

  const exploreImageLoaded = () => {
    loadedImageSrcs.add(label?.src);
    setLoaded(true);
    setIsVideoReadyToPlay(true);
  };

  return (
    <div className="image-container_wrapper">
      <div
        style={{ position: "absolute", top: "0px", height: "100%" }}
        className="image-container"
      >
        <>
          <img
            src={label?.src}
            className={classNames("story-image-container")}
            ref={(img) => {
              if (img && img.complete && img.naturalWidth > 0 && !loaded) {
                exploreImageLoaded();
              }
            }}
            onLoad={exploreImageLoaded}
            style={{ height: "100dvh", objectFit: "cover", top: 0 }}
          />
        </>
        {label && label.content ? (
          <label.content
            onGeoLocationClickHandler={onGeoLocationClickHandler}
            handleCancel={handleCancel}
            handleNextSlide={handleNextSlide}
          />
        ) : null}
      </div>
      {!loaded && (
        <ImageLoader className="loader_wrapper explore_loader_wrapper" />
      )}
      <ToastContainer limit={1} />
    </div>
  );
});

export default memo(LandingStories);
